import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  Users, CheckCircle, Plus, Pencil, Trash2, Search, RefreshCw, FileSpreadsheet
} from "lucide-react";
import { useAdmin } from "./AdminContext";

export function EmployeesTab() {
  const { API, employees, fetchEmployees } = useAdmin();
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({ employee_number: "", name: "" });
  const [employeeSortBy, setEmployeeSortBy] = useState('name');
  const [employeeSortOrder, setEmployeeSortOrder] = useState('asc');
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [deleteEmployeeConfirm, setDeleteEmployeeConfirm] = useState(null);
  const [bulkImportText, setBulkImportText] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [collectingEmployees, setCollectingEmployees] = useState(false);

  const handleSaveEmployee = async () => {
    if (!employeeForm.employee_number || !employeeForm.name) { toast.error("Please fill in all fields"); return; }
    try {
      if (editingEmployee) {
        const id = editingEmployee.id || editingEmployee.employee_number;
        await axios.put(`${API}/admin/employees/${id}`, employeeForm);
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/admin/employees`, employeeForm);
        toast.success("Employee added successfully");
      }
      setShowEmployeeDialog(false); setEditingEmployee(null);
      setEmployeeForm({ employee_number: "", name: "" });
      fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save employee"); }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({ employee_number: employee.employee_number, name: employee.name });
    setShowEmployeeDialog(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${API}/admin/employees/${employeeId}`);
      toast.success("Employee deleted successfully");
      setDeleteEmployeeConfirm(null);
      fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to delete employee"); }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) { toast.error("Please enter employee data"); return; }
    try {
      const lines = bulkImportText.trim().split('\n');
      const employeesToImport = lines.map(line => {
        const parts = line.split(/[,\t]/).map(p => p.trim());
        return parts.length >= 2 ? { employee_number: parts[0], name: parts[1] } : null;
      }).filter(e => e !== null);
      if (employeesToImport.length === 0) { toast.error("No valid employee data found. Use format: ID,Name"); return; }
      const response = await axios.post(`${API}/admin/employees/bulk`, employeesToImport);
      toast.success(response.data.message);
      setBulkImportText(""); setShowBulkImport(false);
      fetchEmployees();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to import employees"); }
  };

  const handleImportFromGuests = async () => {
    try {
      toast.info("Importing employees from your guest records...");
      const response = await axios.post(`${API}/admin/import-from-guests`);
      if (response.data.success) { toast.success(response.data.message); fetchEmployees(); }
      else { toast.error(response.data.message || "Failed to import employees"); }
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to import from guest records"); }
  };

  // Filter and sort
  let filteredEmployees = employees.filter(emp => {
    if (!employeeSearch) return true;
    const search = employeeSearch.toLowerCase();
    return (emp.name || '').toLowerCase().includes(search) || (emp.employee_number || '').toLowerCase().includes(search);
  });
  filteredEmployees = [...filteredEmployees].sort((a, b) => {
    const aVal = employeeSortBy === 'name' ? (a.name || '').toLowerCase() : (a.employee_number || '').toLowerCase();
    const bVal = employeeSortBy === 'name' ? (b.name || '').toLowerCase() : (b.employee_number || '').toLowerCase();
    return employeeSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
  });

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-vault-text tracking-tight">Employee List</h1>
          <p className="text-vault-text-secondary font-manrope mt-1">Manage authorized employees who can check in</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={async () => { try { const res = await axios.post(`${API}/admin/employees/sync-names-to-guests`); toast.success(res.data.message); } catch (error) { toast.error("Failed to sync names"); } }} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="sync-names-to-guests-btn" title="Update guest records with current employee names (for sync agent)">
            <RefreshCw className="w-4 h-4 mr-2" />Sync Names to Guests
          </Button>
          <Button onClick={handleImportFromGuests} disabled={collectingEmployees} className="bg-green-600 hover:bg-green-700 text-white" data-testid="import-from-guests-btn">
            <Users className="w-4 h-4 mr-2" />Import from Guest Records
          </Button>
          <Button onClick={() => setShowBulkImport(true)} className="bg-vault-surface border border-vault-border hover:bg-vault-surface-highlight text-vault-text" data-testid="bulk-import-employees-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Bulk Import
          </Button>
          <Button onClick={() => { setEditingEmployee(null); setEmployeeForm({ employee_number: "", name: "" }); setShowEmployeeDialog(true); }} className="vault-btn-primary" data-testid="add-employee-btn">
            <Plus className="w-4 h-4 mr-2" />Add Employee
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <Card className="bg-vault-surface border-vault-border"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-vault-gold/20 rounded-lg"><Users className="w-5 h-5 text-vault-gold" /></div><div><p className="text-vault-text-secondary text-sm">Total Employees</p><p className="text-2xl font-bold text-vault-text font-mono">{employees.length}</p></div></div></CardContent></Card>
        <Card className="bg-vault-surface border-vault-border"><CardContent className="p-4"><div className="flex items-center gap-3"><div className="p-2 bg-green-500/20 rounded-lg"><CheckCircle className="w-5 h-5 text-green-500" /></div><div><p className="text-vault-text-secondary text-sm">Active Employees</p><p className="text-2xl font-bold text-vault-text font-mono">{employees.filter(e => e.is_active !== false).length}</p></div></div></CardContent></Card>
      </div>

      <Card className="bg-vault-surface border-vault-border">
        <CardHeader className="border-b border-vault-border">
          <div className="flex items-center justify-between">
            <CardTitle className="font-outfit text-xl text-vault-text flex items-center gap-2"><Users className="w-5 h-5 text-vault-gold" />All Employees</CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-vault-text-secondary" />
                <Input placeholder="Search by name or ID..." value={employeeSearch} onChange={(e) => setEmployeeSearch(e.target.value)} className="pl-9 w-48 bg-black/50 border-vault-border text-vault-text text-sm" data-testid="employee-search-input" />
              </div>
              <div className="flex items-center gap-1 text-sm">
                <span className="text-vault-text-secondary">Sort:</span>
                <Button variant="ghost" size="sm" onClick={() => { if (employeeSortBy === 'name') setEmployeeSortOrder(employeeSortOrder === 'asc' ? 'desc' : 'asc'); else { setEmployeeSortBy('name'); setEmployeeSortOrder('asc'); } }} className={`text-xs px-2 ${employeeSortBy === 'name' ? 'text-vault-gold' : 'text-vault-text-secondary'}`} data-testid="sort-by-name-btn">
                  A-Z {employeeSortBy === 'name' && (employeeSortOrder === 'asc' ? '↑' : '↓')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { if (employeeSortBy === 'id') setEmployeeSortOrder(employeeSortOrder === 'asc' ? 'desc' : 'asc'); else { setEmployeeSortBy('id'); setEmployeeSortOrder('asc'); } }} className={`text-xs px-2 ${employeeSortBy === 'id' ? 'text-vault-gold' : 'text-vault-text-secondary'}`} data-testid="sort-by-id-btn">
                  ID {employeeSortBy === 'id' && (employeeSortOrder === 'asc' ? '↑' : '↓')}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[400px]">
            <Table>
              <TableHeader>
                <TableRow className="table-header border-vault-border hover:bg-transparent">
                  <TableHead className="text-vault-gold">Employee ID</TableHead>
                  <TableHead className="text-vault-gold">Name</TableHead>
                  <TableHead className="text-vault-gold">Status</TableHead>
                  <TableHead className="text-vault-gold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-vault-text-secondary py-8">{employeeSearch ? `No employees matching "${employeeSearch}"` : 'No employees added yet.'}</TableCell></TableRow>
                ) : filteredEmployees.map((emp) => (
                  <TableRow key={emp.id} className="table-row border-vault-border" data-testid={`employee-row-${emp.id}`}>
                    <TableCell className="font-mono text-vault-gold font-bold">{emp.employee_number}</TableCell>
                    <TableCell className="text-vault-text">{emp.name}</TableCell>
                    <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${emp.is_active !== false ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>{emp.is_active !== false ? 'Active' : 'Inactive'}</span></TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-vault-gold h-8 w-8 p-0" onClick={() => handleEditEmployee(emp)} data-testid={`edit-employee-${emp.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" className="text-vault-text-secondary hover:text-red-500 h-8 w-8 p-0" onClick={() => setDeleteEmployeeConfirm(emp)} data-testid={`delete-employee-${emp.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      <Dialog open={showEmployeeDialog} onOpenChange={setShowEmployeeDialog}>
        <DialogContent className="bg-vault-surface border-vault-border">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div><label className="text-sm text-vault-text-secondary mb-2 block">Employee ID</label><Input value={employeeForm.employee_number} onChange={(e) => setEmployeeForm({...employeeForm, employee_number: e.target.value})} placeholder="Enter employee ID" className="vault-input" data-testid="employee-id-input" /></div>
            <div><label className="text-sm text-vault-text-secondary mb-2 block">Full Name</label><Input value={employeeForm.name} onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})} placeholder="Enter full name" className="vault-input" data-testid="employee-name-input" /></div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={handleSaveEmployee} className="vault-btn-primary" data-testid="save-employee-btn">{editingEmployee ? 'Update' : 'Add'} Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showBulkImport} onOpenChange={setShowBulkImport}>
        <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Bulk Import Employees</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-vault-text-secondary">Enter employee data, one per line in format: <code className="text-vault-gold">ID,Name</code></p>
            <textarea value={bulkImportText} onChange={(e) => setBulkImportText(e.target.value)} placeholder="EMP001,John Smith&#10;EMP002,Jane Doe" className="w-full h-40 p-3 vault-input font-mono text-sm resize-none" data-testid="bulk-import-textarea" />
            <p className="text-xs text-vault-text-secondary">Duplicate employee IDs will be skipped automatically.</p>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={handleBulkImport} className="vault-btn-primary" data-testid="import-employees-btn">Import Employees</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteEmployeeConfirm} onOpenChange={() => setDeleteEmployeeConfirm(null)}>
        <DialogContent className="bg-vault-surface border-vault-border">
          <DialogHeader><DialogTitle className="font-outfit text-vault-text">Delete Employee</DialogTitle></DialogHeader>
          <p className="text-vault-text-secondary">Are you sure you want to delete employee <span className="text-vault-gold font-mono">{deleteEmployeeConfirm?.employee_number}</span> ({deleteEmployeeConfirm?.name})?</p>
          <DialogFooter>
            <DialogClose asChild><Button variant="ghost" className="text-vault-text-secondary">Cancel</Button></DialogClose>
            <Button onClick={() => handleDeleteEmployee(deleteEmployeeConfirm?.id || deleteEmployeeConfirm?.employee_number)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-employee-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
